import { useEffect, useState } from "react";
import { ShieldCheck, Key, Devices, Copy, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AuthenticatorSetupGuide from "../components/AuthenticatorSetupGuide";

function formatDate(value) {
  if (!value) return "—";
  try { return new Date(value).toLocaleString("pt-BR"); } catch { return String(value); }
}

export default function SecurityPage() {
  const { setUser, refreshMe } = useAuth();
  const [status, setStatus] = useState({ enabled: false, required: false });
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [setupPassword, setSetupPassword] = useState("");
  const [setup, setSetup] = useState(null);
  const [setupCode, setSetupCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, sessionsRes] = await Promise.all([
        api.get("/auth/mfa/status"),
        api.get("/auth/sessions"),
      ]);
      setStatus(statusRes.data);
      setSessions(sessionsRes.data || []);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changePassword = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("As novas senhas não coincidem");
      return;
    }
    try {
      await api.post("/auth/password/change", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(null);
      window.location.href = "/login";
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  const startMfa = async (event) => {
    event.preventDefault();
    try {
      const res = await api.post("/auth/mfa/setup/start", { password: setupPassword });
      setSetup(res.data);
      setSetupCode("");
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  const confirmMfa = async (event) => {
    event.preventDefault();
    try {
      const res = await api.post("/auth/mfa/setup/confirm", {
        setup_id: setup.setup_id,
        code: setupCode,
      });
      setRecoveryCodes(res.data.recovery_codes || []);
      setSetup(null);
      setSetupPassword("");
      setSetupCode("");
      await refreshMe();
      await load();
      toast.success("Autenticação em duas etapas ativada");
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  const disableMfa = async (event) => {
    event.preventDefault();
    try {
      await api.post("/auth/mfa/disable", { password: disablePassword, code: disableCode });
      setDisablePassword("");
      setDisableCode("");
      await refreshMe();
      await load();
      toast.success("Autenticação em duas etapas desativada");
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  const revokeSession = async (sessionId) => {
    try {
      const res = await api.post("/auth/sessions/revoke", { session_id: sessionId });
      if (res.data.current_revoked) {
        setUser(null);
        window.location.href = "/login";
        return;
      }
      toast.success("Sessão encerrada");
      load();
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  const copyCodes = async () => {
    await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Códigos copiados");
  };

  return (
    <main className="max-w-5xl mx-auto px-8 py-10 space-y-8">
      <header>
        <div className="label-eyebrow">conta e proteção</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Segurança.</h1>
        <p className="text-sm text-muted-foreground mt-2">Gerencie senha, autenticação em duas etapas e dispositivos conectados.</p>
      </header>

      <section className="border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <Key size={24} weight="duotone" className="text-primary" />
          <div>
            <h2 className="font-display text-2xl font-black tracking-tight">Alterar senha</h2>
            <p className="text-sm text-muted-foreground">A alteração encerra todas as sessões, inclusive esta.</p>
          </div>
        </div>
        <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input type="password" autoComplete="current-password" placeholder="Senha atual" required
            value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="rounded-none" />
          <Input type="password" autoComplete="new-password" placeholder="Nova senha (mín. 15)" required minLength={15} maxLength={128}
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="rounded-none" />
          <Input type="password" autoComplete="new-password" placeholder="Confirmar nova senha" required minLength={15} maxLength={128}
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="rounded-none" />
          <Button type="submit" className="rounded-none md:col-span-3 md:w-fit">Alterar senha e sair</Button>
        </form>
      </section>

      <section className="border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <ShieldCheck size={24} weight="duotone" className="text-primary" />
          <div>
            <h2 className="font-display text-2xl font-black tracking-tight">Autenticação em duas etapas</h2>
            <p className="text-sm text-muted-foreground">
              Estado: <strong>{status.enabled ? "ativada" : "desativada"}</strong>
              {status.required ? " · obrigatória para seu perfil" : " · opcional para seu perfil"}
            </p>
          </div>
        </div>

        {!status.enabled && !setup && (
          <form onSubmit={startMfa} className="flex flex-col md:flex-row gap-3">
            <Input type="password" autoComplete="current-password" required placeholder="Confirme sua senha atual"
              value={setupPassword} onChange={(e) => setSetupPassword(e.target.value)} className="rounded-none md:max-w-sm" />
            <Button type="submit" className="rounded-none">Configurar aplicativo autenticador</Button>
          </form>
        )}

        {setup && (
          <form onSubmit={confirmMfa} className="space-y-4 max-w-3xl">
            <AuthenticatorSetupGuide
              setupQrDataUrl={setup.qr_data_url}
              manualKey={setup.manual_key}
            />
            <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required
              placeholder="Código de 6 dígitos" value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ""))}
              className="rounded-none text-center tracking-[0.35em]" />
            <div className="flex gap-2">
              <Button type="submit" disabled={setupCode.length !== 6} className="rounded-none">Confirmar ativação</Button>
              <Button type="button" variant="outline" onClick={() => setSetup(null)} className="rounded-none">Cancelar</Button>
            </div>
          </form>
        )}

        {status.enabled && (
          <form onSubmit={disableMfa} className="space-y-3 max-w-lg">
            {status.required && (
              <div className="flex gap-2 text-sm border border-border p-3">
                <Warning size={18} className="shrink-0" />
                A desativação é bloqueada enquanto seu perfil for administrador ou gestor.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input type="password" autoComplete="current-password" required placeholder="Senha atual"
                value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} className="rounded-none" />
              <Input required placeholder="Código do app ou recuperação"
                value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className="rounded-none" />
            </div>
            <Button type="submit" variant="outline" disabled={status.required} className="rounded-none">Desativar autenticação em duas etapas</Button>
          </form>
        )}

        {recoveryCodes.length > 0 && (
          <div className="mt-5 border border-primary p-4">
            <h3 className="font-semibold">Guarde estes códigos agora</h3>
            <p className="text-sm text-muted-foreground mt-1">Eles não serão exibidos novamente e cada um funciona uma única vez.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
              {recoveryCodes.map((code) => <code key={code} className="text-xs text-center">{code}</code>)}
            </div>
            <Button type="button" variant="outline" onClick={copyCodes} className="rounded-none mt-3">
              <Copy size={14} className="mr-2" /> Copiar códigos
            </Button>
          </div>
        )}
      </section>

      <section className="border border-border bg-card">
        <div className="p-6 border-b border-border flex items-center gap-3">
          <Devices size={24} weight="duotone" className="text-primary" />
          <div>
            <h2 className="font-display text-2xl font-black tracking-tight">Sessões ativas</h2>
            <p className="text-sm text-muted-foreground">Encerre acessos em dispositivos que você não reconhece.</p>
          </div>
        </div>
        <div className="divide-y divide-border">
          {loading ? <div className="p-6 text-sm text-muted-foreground">Carregando…</div> : sessions.map((session) => (
            <div key={session.id} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-semibold">
                  {session.client_type === "student_mobile"
                    ? "SAKI/Xm Aluno"
                    : session.current ? "Este dispositivo" : "Outro dispositivo"}
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  {session.client_type === "student_mobile"
                    ? `${session.device_name || "Dispositivo móvel"}${session.app_version ? ` · versão ${session.app_version}` : ""}`
                    : session.user_agent || "Navegador não identificado"}
                </div>
                <div className="text-xs font-mono-data text-muted-foreground mt-1">
                  IP {session.ip || "—"} · último uso {formatDate(session.last_seen_at)} · expira {formatDate(session.expires_at)}
                </div>
              </div>
              <Button variant="outline" onClick={() => revokeSession(session.id)} className="rounded-none shrink-0">
                <Trash size={14} className="mr-2" /> Encerrar
              </Button>
            </div>
          ))}
          {!loading && sessions.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nenhuma sessão ativa.</div>}
        </div>
      </section>
    </main>
  );
}
